import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea/text-area';

import { asTextValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';

export const EditParagraphPlainInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });
	const currentValue = useCurrentValue({ fieldId });
	const [textAreaValue, setTextAreaValue] = useState(currentValue);
	const { updateModifiedValue } = useFormRendererActions();
	const isRequired = useFieldRequired({ fieldId });

	useEffect(() => {
		setTextAreaValue(currentValue);
	}, [currentValue]);

	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const debouncedOnChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setTextAreaValue({ text: event.target.value });
			if (event.target.value.length === 0) {
				debouncedUpdateModifiedValue(fieldId, null);
			} else {
				debouncedUpdateModifiedValue(fieldId, { text: event.target.value });
			}
		},
		[debouncedUpdateModifiedValue, fieldId],
	);

	return (
		<Box>
			<TextArea
				appearance="standard"
				isCompact={false}
				isDisabled={false}
				isReadOnly={false}
				id={fieldInputId}
				value={asTextValue(textAreaValue)?.text ?? ''}
				isInvalid={isInvalid}
				onChange={debouncedOnChange}
				placeholder=""
				testId={`field-input-paragraph-plain-edit-${fieldId}`}
				minimumRows={5}
				maxHeight="none"
				isMonospaced={false}
				defaultValue={undefined}
				resize="smart"
				spellCheck={false}
				onBlur={undefined}
				onFocus={undefined}
				isRequired={isRequired}
				aria-describedby={
					isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
				}
			/>
		</Box>
	);
};
